import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { toast } from "@pps/ui/hooks/use-toast";
import { toast as sonner } from "sonner";
import { MutationsProvider } from "@pps/mutations";
import { TournamentsProvider } from "@pps/tournaments-hooks";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
// M-1 PR-d: AuthProvider vem do AuthBridge — único caminho (sem flag,
// sem fallback legado). Pós cleanup do shim blue-green.
import { AuthProvider } from "@/contexts/AuthBridge";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { NotificationPermission } from "@/components/pwa/NotificationPermission";
import { QuickCheckinFAB } from "@/components/lessons/QuickCheckinFAB";
import { NetworkStatusBanner } from "@/components/NetworkStatusBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import { RedirecionaParaAdmin } from "@pps/ui/components/sistema";
import { ImpersonationBannerWrapper } from "@/components/ImpersonationBannerWrapper";
import { PlatformBannerDisplay } from "@/components/PlatformBannerDisplay";
import { SaudeDoEsquema } from "@/components/sistema/SaudeDoEsquema";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const Reservar = lazy(() => import("./pages/Reservar"));
const Jogar = lazy(() => import("./pages/Jogar"));
const Perfil = lazy(() => import("./pages/Perfil"));
const MinhasAulas = lazy(() => import("./pages/MinhasAulas"));
const MinhasReposicoes = lazy(() => import("./pages/MinhasReposicoes"));
const MinhasFaturas = lazy(() => import("./pages/MinhasFaturas"));
const Jogador = lazy(() => import("./pages/Jogador"));
const Clubes = lazy(() => import("./pages/Clubes"));
const ClubePublico = lazy(() => import("./pages/ClubePublico"));
const ClubeCategoria = lazy(() => import("./pages/ClubeCategoria"));
const Auth = lazy(() => import("./pages/Auth"));
const Torneios = lazy(() => import("./pages/Torneios"));
const TorneioDetalhe = lazy(() => import("./pages/TorneioDetalhe"));
const SolicitarClube = lazy(() => import("./pages/SolicitarClube"));
const ParaClubes = lazy(() => import("./pages/ParaClubes"));
const ParaJogadores = lazy(() => import("./pages/ParaJogadores"));
const PartidaAoVivo = lazy(() => import("./pages/PartidaAoVivo"));
const Apresentacao = lazy(() => import("./pages/Apresentacao"));
const ApresentacaoCorporativa = lazy(() => import("./pages/ApresentacaoCorporativa"));
const Termos = lazy(() => import("./pages/Termos"));
const IndicarClube = lazy(() => import("./pages/IndicarClube"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TorneioTV = lazy(() => import("./pages/TorneioTV"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Configure React Query with retry logic and stale time for resilience
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

/**
 * O portal tem a rota `/jogador/:userId` (ver <Routes> abaixo), o CRM não —
 * por isso `@pps/tournaments-ui` não assume rota nenhuma e recebe o resolver
 * daqui. Identidade referencial estável: const fora do componente evita
 * recriar o `value` do provider a cada render.
 */
const playerHref = (playerId: string) => `/jogador/${playerId}`;
// O portal resolve o placar ao vivo só com a partida; o torneio vem no
// segundo argumento por causa do CRM, e aqui é ignorado de propósito.
const liveMatchHref = (matchId: string) => `/partida/${matchId}/live`;

function TournamentsProviderBridge({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return (
    <TournamentsProvider
      client={supabase}
      userId={user?.id ?? null}
      playerHref={playerHref}
      liveMatchHref={liveMatchHref}
    >
      {children}
    </TournamentsProvider>
  );
}

const App = () => {
  // Global handler for unhandled promise rejections
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro. Tente recarregar a página.",
        variant: "destructive",
      });
      event.preventDefault();
    };
    
    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return (
  <ErrorBoundary>
    {/* Banco atrás deste build → "Sistema em atualização" no lugar do app inteiro
        (docs/funcionalidades/versao-do-app-e-do-esquema.md). */}
    <SaudeDoEsquema>
    <QueryClientProvider client={queryClient}>
      <MutationsProvider
        toast={({ title, description, variant }) => {
          // M-5b batch 9 (CF-8): adapter sonner para useDomainMutation no app public.
          // Mensagens neutras em fluxos de pagamento — nunca leak de valores monetarios.
          if (variant === "error") sonner.error(title, { description });
          else if (variant === "info") sonner.info(title, { description });
          else sonner.success(title, { description });
        }}
      >
      <AuthProvider>
        <TournamentsProviderBridge>
        <TooltipProvider>
          <ImpersonationBannerWrapper />
          <PlatformBannerDisplay />
          <Toaster />
          <Sonner />
          <InstallPrompt />
          <NotificationPermission />
          <ErrorBoundary fallback={<></>}>
            <QuickCheckinFAB />
          </ErrorBoundary>
          <NetworkStatusBanner />
          <Analytics />
          <SpeedInsights />
          <BrowserRouter>
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
              <Routes>
                <Route path="/" element={<RouteErrorBoundary><Index /></RouteErrorBoundary>} />
                <Route path="/auth" element={<RouteErrorBoundary><Auth /></RouteErrorBoundary>} />
                <Route path="/reservar" element={<RouteErrorBoundary><Reservar /></RouteErrorBoundary>} />
                <Route path="/jogar" element={<RouteErrorBoundary><Jogar /></RouteErrorBoundary>} />
                <Route path="/perfil" element={<RouteErrorBoundary><Perfil /></RouteErrorBoundary>} />
                <Route path="/minhas-aulas" element={<RouteErrorBoundary><MinhasAulas /></RouteErrorBoundary>} />
                <Route path="/minhas-reposicoes" element={<RouteErrorBoundary><MinhasReposicoes /></RouteErrorBoundary>} />
                <Route path="/minhas-faturas" element={<RouteErrorBoundary><MinhasFaturas /></RouteErrorBoundary>} />
                <Route path="/jogador/:userId" element={<RouteErrorBoundary><Jogador /></RouteErrorBoundary>} />
                <Route path="/clubes" element={<RouteErrorBoundary><Clubes /></RouteErrorBoundary>} />
                <Route path="/clubes/:id" element={<RouteErrorBoundary><ClubePublico /></RouteErrorBoundary>} />
                <Route path="/clubes/:id/categoria/:categoryId" element={<RouteErrorBoundary><ClubeCategoria /></RouteErrorBoundary>} />
                <Route path="/solicitar-acesso-clube" element={<RouteErrorBoundary><SolicitarClube /></RouteErrorBoundary>} />
                <Route path="/para-clubes" element={<RouteErrorBoundary><ParaClubes /></RouteErrorBoundary>} />
                <Route path="/para-jogadores" element={<RouteErrorBoundary><ParaJogadores /></RouteErrorBoundary>} />
                {/* /admin/* é do admin da PPS (apps/admin): redireciona sempre, preservando o caminho. */}
                <Route path="/admin/*" element={<RedirecionaParaAdmin ambiente={import.meta.env.VITE_APP_ENV} />} />
                <Route path="/torneios" element={<RouteErrorBoundary><Torneios /></RouteErrorBoundary>} />
                <Route path="/torneios/:id" element={<RouteErrorBoundary><TorneioDetalhe /></RouteErrorBoundary>} />
                <Route path="/torneio/:id" element={<RouteErrorBoundary><TorneioDetalhe /></RouteErrorBoundary>} />
                <Route path="/partida/:matchId/live" element={<RouteErrorBoundary><PartidaAoVivo /></RouteErrorBoundary>} />
                <Route path="/apresentacao" element={<RouteErrorBoundary><Apresentacao /></RouteErrorBoundary>} />
                <Route path="/apresentacao-corporativa" element={<RouteErrorBoundary><ApresentacaoCorporativa /></RouteErrorBoundary>} />
                <Route path="/termos" element={<RouteErrorBoundary><Termos /></RouteErrorBoundary>} />
                <Route path="/indicar-clube" element={<RouteErrorBoundary><IndicarClube /></RouteErrorBoundary>} />
                <Route path="/reset-password" element={<RouteErrorBoundary><ResetPassword /></RouteErrorBoundary>} />
                <Route path="/torneio/:id/tv" element={<RouteErrorBoundary><TorneioTV /></RouteErrorBoundary>} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                {/* `/cobranca/:token` (link durável da assinatura Stripe) e o
                    desfecho `/cobranca/sucesso|cancelado` saíram em 11/10/2026,
                    com o Stripe desligado: a Edge `billing-recurring-checkout`
                    virou stub 410. A mensalidade automática é o link RECURRENT
                    do Asaas, que vive na página do próprio Asaas. */}
                <Route path="*" element={<RouteErrorBoundary><NotFound /></RouteErrorBoundary>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
        </TournamentsProviderBridge>
      </AuthProvider>
      </MutationsProvider>
    </QueryClientProvider>
    </SaudeDoEsquema>
  </ErrorBoundary>
  );
};

export default App;
